import { useState } from 'react';
import { Plus, Pencil, Trash2, Lock, Loader2, Check, X, ShieldCheck } from 'lucide-react';
import { useContactCategories } from '@/features/crm/hooks/useContacts';
import {
  useCreateCategory,
  useCreatePlatformCategory,
  useUpdateCategory,
  useDeleteCategory,
} from '@/features/crm/hooks/useCategoryMutations';
import { useAuth } from '@/app/providers/AuthProvider';
import { ColorPicker } from './ColorPicker';
import { COLOR_PALETTE, type ContactCategoryRow } from '@/features/crm/types';

/* ─── Linha editável ─────────────────────────────────────────────────────────── */
function CategoryRow({ cat, isSuperAdmin }: { cat: ContactCategoryRow; isSuperAdmin: boolean }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(cat.label);
  const [color, setColor] = useState(cat.color_class);

  const updateMutation = useUpdateCategory();
  const deleteMutation = useDeleteCategory();

  const isPlatformDefault = cat.tenant_id === null;
  const canEdit = !isPlatformDefault || isSuperAdmin;

  const handleSave = () => {
    if (!label.trim()) return;
    updateMutation.mutate(
      { id: cat.id, label: label.trim(), color_class: color },
      { onSuccess: () => setEditing(false) },
    );
  };

  const handleDelete = () => {
    const msg = isPlatformDefault
      ? `Remover o padrão de plataforma "${cat.label}"? Todos os tenants perderão essa categoria.`
      : `Remover a categoria "${cat.label}"? Contatos com esta categoria ficarão sem classificação.`;
    if (!window.confirm(msg)) return;
    deleteMutation.mutate(cat.id);
  };

  const handleCancel = () => {
    setLabel(cat.label);
    setColor(cat.color_class);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="card-premium rounded-xl p-4 space-y-3 border-accent/20">
        <div className="space-y-1">
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">
            Nome da categoria
          </label>
          <input
            autoFocus
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') handleCancel(); }}
            className="w-full rounded-xl bg-midnight-2/60 border border-accent/40 px-3 py-2 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/70 transition-all"
          />
        </div>

        <div className="space-y-2">
          <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Cor</label>
          <ColorPicker value={color} onChange={setColor} />
        </div>

        <div className="flex items-center gap-2">
          <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">Preview:</span>
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${color}`}>
            {label || 'Categoria'}
          </span>
        </div>

        <div className="flex gap-2 pt-1">
          <button
            type="button"
            onClick={handleSave}
            disabled={updateMutation.isPending || !label.trim()}
            className="flex items-center gap-1.5 rounded-lg btn-accent px-3 py-1.5 text-xs font-semibold cursor-pointer disabled:opacity-50"
          >
            {updateMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            Salvar
          </button>
          <button
            type="button"
            onClick={handleCancel}
            className="rounded-lg btn-ghost px-3 py-1.5 text-xs font-semibold cursor-pointer"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border border-hairline transition-colors ${
      isPlatformDefault && !isSuperAdmin
        ? 'bg-midnight-2/20 opacity-70'
        : 'bg-midnight-2/40 hover:bg-midnight-2/60'
    }`}>
      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider shrink-0 ${cat.color_class}`}>
        {cat.label}
      </span>

      <div className="flex-grow min-w-0">
        <p className="text-sm font-semibold text-ink truncate">{cat.label}</p>
        {isPlatformDefault && !isSuperAdmin && (
          <p className="text-[10px] text-ink-subtle flex items-center gap-1 mt-0.5">
            <Lock className="h-3 w-3" />
            Padrão da plataforma — somente leitura
          </p>
        )}
        {isPlatformDefault && isSuperAdmin && (
          <p className="text-[10px] text-accent flex items-center gap-1 mt-0.5">
            <ShieldCheck className="h-3 w-3" />
            Padrão de plataforma — visível a todos os tenants
          </p>
        )}
      </div>

      {canEdit && (
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => setEditing(true)}
            className="rounded-lg p-1.5 text-ink-muted hover:bg-white/5 hover:text-ink transition-colors cursor-pointer"
            title="Editar"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            className="rounded-lg p-1.5 text-ink-muted hover:bg-danger/10 hover:text-danger transition-colors cursor-pointer disabled:opacity-40"
            title="Remover"
          >
            {deleteMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      )}
    </div>
  );
}

/* ─── Formulário genérico de nova categoria ──────────────────────────────────── */
function NewCategoryForm({
  existingCount,
  platformDefault = false,
}: {
  existingCount: number;
  platformDefault?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState('');
  const [color, setColor] = useState(COLOR_PALETTE[existingCount % COLOR_PALETTE.length]);

  const createTenant   = useCreateCategory();
  const createPlatform = useCreatePlatformCategory();
  const mutation = platformDefault ? createPlatform : createTenant;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    mutation.mutate(
      { label: label.trim(), colorIndex: existingCount },
      {
        onSuccess: () => {
          setLabel('');
          setColor(COLOR_PALETTE[(existingCount + 1) % COLOR_PALETTE.length]);
          setOpen(false);
        },
      },
    );
  };

  const btnLabel = platformDefault ? 'Novo padrão de plataforma' : 'Nova categoria personalizada';
  const createLabel = platformDefault ? 'Criar padrão' : 'Criar categoria';

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="flex items-center gap-2 w-full rounded-xl border border-dashed border-hairline px-4 py-3 text-sm text-ink-muted hover:border-accent/40 hover:text-accent transition-all cursor-pointer"
      >
        <Plus className="h-4 w-4" />
        {btnLabel}
      </button>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="card-premium rounded-xl p-4 space-y-3 border-accent/20">
      <p className="text-sm font-semibold text-ink">{platformDefault ? 'Novo padrão de plataforma' : 'Nova categoria'}</p>

      <div className="space-y-1">
        <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Nome</label>
        <input
          autoFocus
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Ex.: Vereador, Pastor, Empresário…"
          className="w-full rounded-xl bg-midnight-2/60 border border-hairline px-3 py-2 text-sm text-ink placeholder-ink-faint focus:outline-none focus:border-accent/50 transition-all"
        />
      </div>

      <div className="space-y-2">
        <label className="font-mono text-[10px] uppercase tracking-[0.18em] font-semibold text-ink-muted">Cor</label>
        <ColorPicker value={color} onChange={setColor} />
      </div>

      <div className="flex items-center gap-2">
        <span className="font-mono text-[10px] uppercase tracking-wider text-ink-subtle">Preview:</span>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${color}`}>
          {label || 'Categoria'}
        </span>
      </div>

      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={mutation.isPending || !label.trim()}
          className="flex items-center gap-1.5 rounded-lg btn-accent px-3 py-1.5 text-xs font-semibold cursor-pointer disabled:opacity-50"
        >
          {mutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          {createLabel}
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); setLabel(''); }}
          className="rounded-lg btn-ghost px-3 py-1.5 text-xs font-semibold cursor-pointer"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}

/* ─── Página principal ────────────────────────────────────────────────────────── */
export function CategoriesSettings() {
  const { isSuperAdmin } = useAuth();
  const { data: categories = [], isLoading } = useContactCategories();

  const defaults = categories.filter((c) => c.tenant_id === null);
  const custom   = categories.filter((c) => c.tenant_id !== null);

  return (
    <div className="space-y-8 max-w-xl">
      {/* Intro */}
      <div className="card-premium rounded-2xl px-5 py-4">
        <p className="text-sm text-ink-muted leading-relaxed">
          Categorias classificam seus contatos (ex.: Eleitor, Liderança, Vereador).
          Os <strong className="text-ink">padrões de plataforma</strong> estão disponíveis para todos os tenants
          {isSuperAdmin
            ? ' e podem ser editados por superadmins.'
            : ' e não podem ser editados. Crie categorias personalizadas para o seu contexto.'}
        </p>
      </div>

      {isLoading ? (
        <div className="flex items-center gap-3 text-ink-muted py-8 justify-center">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm">Carregando categorias…</span>
        </div>
      ) : (
        <>
          {/* Padrões da plataforma */}
          <section className="space-y-3">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-subtle flex items-center gap-2">
              <span className="flex-grow hairline-b pb-1">
                Padrões da plataforma
                {isSuperAdmin && (
                  <span className="ml-2 font-mono text-[9px] bg-accent/15 text-accent border border-accent/25 rounded-full px-2 py-0.5 normal-case tracking-normal inline-flex items-center gap-1">
                    <ShieldCheck className="h-2.5 w-2.5" /> editável
                  </span>
                )}
              </span>
            </h2>
            <div className="space-y-2">
              {defaults.map((cat) => (
                <CategoryRow key={cat.id} cat={cat} isSuperAdmin={isSuperAdmin} />
              ))}
            </div>
            {isSuperAdmin && (
              <NewCategoryForm existingCount={defaults.length} platformDefault />
            )}
          </section>

          {/* Categorias personalizadas */}
          <section className="space-y-3">
            <h2 className="font-mono text-[10px] uppercase tracking-[0.2em] font-semibold text-ink-subtle flex items-center gap-2">
              <span className="flex-grow hairline-b pb-1">
                Personalizadas
                {custom.length > 0 && (
                  <span className="ml-2 font-mono text-[9px] bg-accent/15 text-accent border border-accent/25 rounded-full px-2 py-0.5 normal-case tracking-normal">
                    {custom.length}
                  </span>
                )}
              </span>
            </h2>

            <div className="space-y-2">
              {custom.length === 0 && (
                <p className="text-sm text-ink-subtle px-1">Nenhuma categoria personalizada ainda.</p>
              )}
              {custom.map((cat) => (
                <CategoryRow key={cat.id} cat={cat} isSuperAdmin={isSuperAdmin} />
              ))}
            </div>

            <NewCategoryForm existingCount={custom.length} />
          </section>
        </>
      )}
    </div>
  );
}
